import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceToNow, isPast, parseISO } from 'date-fns';
import UserDropdown from '../UserDropdown';

// Formatting helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const formatNumber = (num, decimals = 0) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num || 0);
};

const PayoutHistory = () => {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [clientMetrics, setClientMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this_year');

  const fetchPayoutData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all payout data in parallel
      const [historyRes, summaryRes, pendingRes, clientMetricsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/payouts/history?period=${selectedPeriod}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/payouts/summary?period=${selectedPeriod}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/payouts/pending`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/payouts/client-metrics?period=${selectedPeriod}`, { headers })
      ]);

      setPayouts(historyRes.data.payouts || []);
      setSummary(summaryRes.data);
      setPendingPayouts(pendingRes.data.pending_payouts || []);
      setClientMetrics(clientMetricsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load payout data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchPayoutData();
  }, [fetchPayoutData]);

  // Navigator+ tier check (tier_level 1 or 2)
  const isNavigatorOrHigher = summary?.tier_level && summary.tier_level <= 2;

  // Locked metric overlay component
  const LockedMetric = () => (
    <div className="absolute inset-0 bg-gray-900/80 rounded-lg flex items-center justify-center backdrop-blur-sm">
      <div className="text-center px-4">
        <svg className="w-8 h-8 mx-auto mb-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-white text-sm font-medium">Upgrade to Navigator</p>
        <p className="text-gray-300 text-xs">to see this metric!</p>
      </div>
    </div>
  );

  // Format estimated available date
  const formatEstimatedDate = (dateString) => {
    if (!dateString) return '—';
    const date = parseISO(dateString);
    if (isPast(date)) {
      return 'Soon';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Navigation sections
  const navSections = [
    { id: 'earnings-breakdown', label: 'Earnings' },
    { id: 'tips-breakdown', label: 'Tips' },
    { id: 'performance-metrics', label: 'Performance' },
    { id: 'client-metrics', label: 'Clients' },
    { id: 'pending-payouts', label: 'Pending' },
    { id: 'transaction-history', label: 'History' }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  // Calculate derived metrics
  const totalFareProcessingFees = summary?.total_fare_processing_fees || 0;
  const totalTipProcessingFees = summary?.total_tip_processing_fees || 0;
  const totalProcessingFees = totalFareProcessingFees + totalTipProcessingFees;
  // Total charged includes fares, tips, and all processing fees (what riders actually paid)
  const totalCharged = (summary?.total_fares || 0) + (summary?.total_tips_gross || 0) + totalProcessingFees;
  const totalPlatformFees = summary?.total_platform_commission || 0;
  const totalPlatformFeeTax = summary?.total_platform_fee_tax || 0;
  const totalEarnings = summary?.total_earnings || 0;
  const totalFarePayout = summary?.total_fare_payout || 0;
  const totalTipPayout = summary?.total_tip_payout || 0;
  const totalMileage = summary?.total_mileage || 0;
  const totalRides = summary?.total_rides || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/driver/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Payout History</h1>
            </div>
            <UserDropdown userType="driver" />
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="bg-white border-b sticky top-[72px] z-10 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex gap-2">
          {navSections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-md whitespace-nowrap transition-colors"
            >
              {section.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page description */}
        <div className="mb-8">
          <p className="text-gray-600">View your earnings, pending payouts, and transaction history. Earnings shown on <strong>Paid Out Amounts Only</strong>, for all earnings regardless of status see Ride History page.</p>
        </div>

      {/* KPI Dashboard */}
      {summary && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Earnings Summary</h2>
            <div className="flex items-center gap-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="all">All Time</option>
              </select>
              <span
                className="cursor-help text-gray-400 hover:text-gray-600"
                title="Dates are calendar-based. Weeks run Monday through Sunday."
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
          </div>

          {/* ROW 1: Earnings Equation (Always Visible) */}
          <div id="earnings-breakdown" className="mb-6 scroll-mt-32">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-600">Earnings Breakdown</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-9 gap-2 items-center">
              {/* Total Charged */}
              <div className="md:col-span-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
                <h3 className="text-xs font-medium opacity-90 mb-1">Total Charged to Customers</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalCharged)}</p>
                <p className="text-xs opacity-70 mt-1">{formatNumber(totalRides)} paid rides</p>
              </div>

              {/* Minus */}
              <div className="hidden md:flex items-center justify-center text-2xl font-bold text-gray-400">−</div>

              {/* Platform Fees */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 text-white">
                <h3 className="text-xs font-medium opacity-90 mb-1">Platform Fees</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalPlatformFees)}</p>
              </div>

              {/* Minus */}
              <div className="hidden md:flex items-center justify-center text-2xl font-bold text-gray-400">−</div>

              {/* Platform Fee Tax */}
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-4 text-white">
                <h3 className="text-xs font-medium opacity-90 mb-1">Platform Fee Tax</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalPlatformFeeTax)}</p>
              </div>

              {/* Minus */}
              <div className="hidden md:flex items-center justify-center text-2xl font-bold text-gray-400">−</div>

              {/* Stripe Processing Fees */}
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-4 text-white">
                <h3 className="text-xs font-medium opacity-90 mb-1">Stripe Fees</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalProcessingFees)}</p>
              </div>

              {/* Equals */}
              <div className="hidden md:flex items-center justify-center text-2xl font-bold text-gray-400">=</div>

              {/* Total Earnings */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4 text-white">
                <h3 className="text-xs font-medium opacity-90 mb-1">Total Earnings</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalEarnings)}</p>
                <p className="text-xs opacity-70 mt-1">Your payout</p>
              </div>
            </div>
          </div>

          {/* ROW 2: Tips Breakdown (Tier-Gated) */}
          <div id="tips-breakdown" className="mb-6 scroll-mt-32">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-600">Tips Breakdown</span>
              {!isNavigatorOrHigher && (
                <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">Navigator+</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center relative">
              {/* Total Earnings */}
              <div className={`bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4 text-white ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                <h3 className="text-xs font-medium opacity-90 mb-1">Total Earnings</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalEarnings)}</p>
              </div>

              {/* Minus */}
              <div className={`hidden md:flex items-center justify-center text-2xl font-bold text-gray-400 ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>−</div>

              {/* Total Fare Payout */}
              <div className={`bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-4 text-white ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                <h3 className="text-xs font-medium opacity-90 mb-1">Total Fares</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalFarePayout)}</p>
                <p className="text-xs opacity-70 mt-1">Net of fees</p>
              </div>

              {/* Equals */}
              <div className={`hidden md:flex items-center justify-center text-2xl font-bold text-gray-400 ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>=</div>

              {/* Total Tips Payout */}
              <div className={`bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg shadow-lg p-4 text-white ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                <h3 className="text-xs font-medium opacity-90 mb-1">Total Tips</h3>
                <p className="text-2xl font-bold">${formatCurrency(totalTipPayout)}</p>
                <p className="text-xs opacity-70 mt-1">Net of fees</p>
              </div>

              {!isNavigatorOrHigher && <LockedMetric />}
            </div>
          </div>

          {/* ROW 3: Performance Metrics (Tier-Gated) */}
          <div id="performance-metrics" className="mb-6 scroll-mt-32">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-600">Performance Metrics</span>
              {!isNavigatorOrHigher && (
                <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">Navigator+</span>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 relative overflow-hidden">
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                {/* Total Miles Driven */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900">{formatNumber(totalMileage, 1)}</div>
                  <div className="text-sm text-gray-600">Total Miles Driven</div>
                </div>

                {/* Earnings Per Mile */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">
                    ${totalMileage > 0 ? formatCurrency(totalEarnings / totalMileage) : '0.00'}
                  </div>
                  <div className="text-sm text-gray-600">Earnings Per Mile</div>
                </div>

                {/* Tips Per Mile */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-pink-600">
                    ${totalMileage > 0 ? formatCurrency(totalTipPayout / totalMileage) : '0.00'}
                  </div>
                  <div className="text-sm text-gray-600">Tips Per Mile</div>
                </div>
              </div>

              {/* Second row of metrics */}
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                {/* Miles Per Trip */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-indigo-600">
                    {totalRides > 0 ? formatNumber(totalMileage / totalRides, 1) : '0.0'}
                  </div>
                  <div className="text-sm text-gray-600">Miles Per Trip</div>
                </div>

                {/* Earnings Per Trip */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">
                    ${totalRides > 0 ? formatCurrency(totalEarnings / totalRides) : '0.00'}
                  </div>
                  <div className="text-sm text-gray-600">Earnings Per Trip</div>
                </div>

                {/* Tips Per Trip */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">
                    ${totalRides > 0 ? formatCurrency(totalTipPayout / totalRides) : '0.00'}
                  </div>
                  <div className="text-sm text-gray-600">Tips Per Trip</div>
                </div>
              </div>

              {!isNavigatorOrHigher && <LockedMetric />}
            </div>
          </div>

          {/* ROW 4: Client Metrics (Tier-Gated) */}
          <div id="client-metrics" className="mb-6 scroll-mt-32">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-600">Client Metrics</span>
              {!isNavigatorOrHigher && (
                <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">Navigator+</span>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-lg p-4 relative overflow-hidden">
              {clientMetrics?.top_riders?.length > 0 ? (
                <div className={`${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Top 5 Riders by Earnings</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Rider</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Total Earnings</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Trips</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Miles</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">$/Mile</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">$/Trip</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Fare/Mile</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Fare/Trip</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Tips/Mile</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Tips/Trip</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {clientMetrics.top_riders.map((rider, index) => (
                          <tr key={rider.rider_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{rider.rider_name}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 font-semibold">${formatCurrency(rider.total_earnings)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(rider.total_trips)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">{formatNumber(rider.total_miles, 1)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">${formatCurrency(rider.earnings_per_mile)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">${formatCurrency(rider.earnings_per_trip)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-indigo-600">${formatCurrency(rider.fare_per_mile)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-indigo-600">${formatCurrency(rider.fare_per_trip)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-pink-600">${formatCurrency(rider.tips_per_mile)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-pink-600">${formatCurrency(rider.tips_per_trip)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className={`text-center py-8 ${!isNavigatorOrHigher ? 'blur-sm' : ''}`}>
                  <p className="text-gray-500">No client data available for this period</p>
                </div>
              )}
              {!isNavigatorOrHigher && <LockedMetric />}
            </div>
          </div>

          {/* Pending Amount Banner */}
          {summary.pending_amount > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <span className="text-purple-900 font-medium">Pending Payout</span>
                    <p className="text-purple-700 text-sm">{formatNumber(pendingPayouts.length)} ride{pendingPayouts.length !== 1 ? 's' : ''} awaiting processing</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-purple-600">${formatCurrency(summary.pending_amount)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Payouts Table */}
      <div id="pending-payouts" className="scroll-mt-32">
        {pendingPayouts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Pending Payouts</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-yellow-800 font-medium">
                    You have {formatNumber(pendingPayouts.length)} completed ride{pendingPayouts.length !== 1 ? 's' : ''} awaiting payout processing
                  </p>
                  <p className="text-yellow-700 text-sm mt-1">
                    Payouts are typically transferred within 2-3 business days of ride completion. Transfers may take up to 2 business days to reach your bank account.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-600 text-sm">
                  Note: Pending payouts are not affected by the time period filter above.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ride</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Est. Transfer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingPayouts.map((ride) => (
                    <tr key={`${ride.booking_type}-${ride.ride_id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {ride.pickup_address} → {ride.dropoff_address}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {ride.ride_date
                            ? format(new Date(ride.ride_date), 'MMM dd, yyyy')
                            : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {ride.ride_date
                            ? format(new Date(ride.ride_date), 'h:mm a')
                            : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          ${formatCurrency(ride.driver_payout)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {ride.estimated_available_date
                            ? format(new Date(ride.estimated_available_date), 'MMM dd')
                            : '—'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatEstimatedDate(ride.estimated_available_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Payout History */}
      <div id="transaction-history" className="scroll-mt-32">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Transaction History</h2>

        {payouts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payout history yet</h3>
            <p className="text-gray-600">Complete rides to start earning and receiving payouts</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ride</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ride Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform Fee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payouts.map((payout) => (
                  <tr key={`${payout.booking_type}-${payout.ride_id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payout.payout_date
                          ? format(new Date(payout.payout_date), 'MMM dd, yyyy')
                          : 'Pending'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {payout.payout_date
                          ? format(new Date(payout.payout_date), 'h:mm a')
                          : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {payout.pickup_address} → {payout.dropoff_address}
                      </div>
                      <div className="text-xs text-gray-500">
                        Ride #{payout.ride_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${formatCurrency(payout.total_fare)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payout.tip_amount && payout.tip_amount > 0 ? `$${formatCurrency(payout.tip_amount)}` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      -${formatCurrency(payout.platform_fee)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      -${formatCurrency(payout.platform_fee_tax)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        ${formatCurrency(payout.driver_payout)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500 font-mono max-w-xs truncate">
                        {payout.payout_transfer_id || '—'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </main>
    </div>
  );
};

export default PayoutHistory;
